import { useEffect, useState } from 'react'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'
import { SignedIn, SignedOut, useAuth } from '@clerk/clerk-react'
import { AuthIntermediator } from '@/components/AuthIntermediator'
import Loading from '@/components/Loading'
import { ErrorComponent } from '@/components/error'
import { Button } from '@/components/common/Button'

const CLERK_LOAD_TIMEOUT = 20_000

// Clerk is loaded from a third-party CDN. Until it resolves `userId` is
// undefined, and *both* <SignedIn> and <SignedOut> render null on undefined —
// so a CDN that is blocked, throttled or down leaves a blank page with no
// spinner, no redirect to /login and nothing logged. Hold the route here until
// Clerk settles, and say so plainly if it never does.
const ClerkLoadGate = () => {
  const [timedOut, setTimedOut] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setTimedOut(true), CLERK_LOAD_TIMEOUT)
    return () => clearTimeout(timer)
  }, [])

  if (!timedOut) {
    return <Loading message="Loading..." />
  }

  return (
    <ErrorComponent
      title={'Cannot reach sign-in'}
      subTitle={'We could not load the sign-in service. Check your connection and try again.'}
    >
      <Button primary onClick={() => window.location.reload()}>
        {'Reload'}
      </Button>
    </ErrorComponent>
  )
}

// Redirects signed-out users to /login, preserving the current path for
// post-auth redirect when the destination is a shardlibrary route.
const RedirectToLogin = () => {
  const navigate = useNavigate()

  useEffect(() => {
    const path = window.location.pathname
    const redirectParam = path.startsWith('/shardlibrary')
      ? `?redirect_url=${encodeURIComponent(path)}`
      : ''
    navigate({ to: `/login${redirectParam}`, replace: true })
  }, [navigate])

  return <Loading />
}

const AuthLayout = () => {
  const { isLoaded } = useAuth()

  if (!isLoaded) {
    return <ClerkLoadGate />
  }

  return (
    <>
      <SignedIn>
        <AuthIntermediator>
          <Outlet />
        </AuthIntermediator>
      </SignedIn>
      <SignedOut>
        <RedirectToLogin />
      </SignedOut>
    </>
  )
}

export const Route = createFileRoute('/_auth')({
  component: AuthLayout,
})
